/**
 * AdminLayout — shell for all /admin/* routes.
 *
 * Sidebar navigation: Users / Content / Health
 * Role guard: shows 403 forbidden page for non-admins.
 */
import { NavLink, Outlet } from 'react-router-dom'
import { useUser } from '@/store'
import { cn } from '@/lib/utils'
import { Users, FileEdit, Activity, ShieldAlert, ChevronLeft, ShieldX } from 'lucide-react'
import { Link } from 'react-router-dom'

const NAV_ITEMS = [
  { to: '/admin/users',   label: 'Users',   icon: <Users   size={15} /> },
  { to: '/admin/content', label: 'Content', icon: <FileEdit size={15} /> },
  { to: '/admin/health',  label: 'Health',  icon: <Activity size={15} /> },
]

function ForbiddenPage() {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4 text-center px-4">
      <ShieldX size={40} className="text-inauspicious/60" />
      <h2 className="text-xl font-bold text-star-bright">Access Denied</h2>
      <p className="text-sm text-star-muted max-w-xs">
        You do not have permission to access the admin panel. Contact an administrator if you believe this is an error.
      </p>
      <Link to="/dashboard" className="mt-2 text-xs text-gold-dim hover:text-gold underline transition-colors">
        ← Back to Dashboard
      </Link>
    </div>
  )
}

export function AdminLayout() {
  const user = useUser()

  // Role guard — backend enforces this too; frontend shows 403 early
  if (!user || user.role?.toLowerCase() !== 'admin') {
    return (
      <div className="flex min-h-screen bg-cosmic-void">
        <main className="flex-1 overflow-auto">
          <ForbiddenPage />
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-cosmic-void">
      {/* Admin sidebar */}
      <aside className="w-52 flex-shrink-0 bg-cosmic-nebula border-r border-cosmic-mist flex flex-col">
        {/* Header */}
        <div className="px-4 py-4 border-b border-cosmic-mist">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert size={16} className="text-gold-dim" />
            <span className="text-xs font-mono uppercase tracking-wider text-star-bright">Admin</span>
          </div>
          <Link to="/dashboard"
            className="flex items-center gap-1.5 text-2xs text-star-dim hover:text-star-bright transition-colors">
            <ChevronLeft size={11} /> Back to App
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-cosmic-dusk text-star-bright'
                  : 'text-star-muted hover:bg-cosmic-dusk/50 hover:text-star-bright',
              )}
            >
              <span className="text-gold-dim">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Page content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
